/* eslint-disable max-lines */
import useClassInfo from '@api/hooks/class/useClassInfo';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getClass, goBack } from '@utils/breadcrumbs';
import useConstants from '@api/hooks/useConstants';
import useCourseInfo from '@api/hooks/course/useCourseInfo';
import { getIsGTPGoal } from '@api/selectors/constants';
import Tag from 'aquilla/core/atoms/Tag';
import { getAuthorName, LANGUAGE_MAP } from '@cont/Content/FreeLesson/utils';
import Typography from 'aquilla/core/atoms/Typography';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import Link from '@base/Link/Link';
import EducatorAvatar from '@cont/EducatorProfile/EducatorAvatar';
import { nFormatter } from '@utils/textHelper';
import {
  classCancelledMsg,
  classDidNotHappen,
  getSpecialInfoString,
  isCurrentlyLive,
  isCompletedWithNoVideoUrl,
  isUpcoming as getIsUpcoming
} from '@models/Item';
import Menu from '@base/Menu/Menu';
import PDFDownload from '@cont/Course/PDFDownload';
import Forward from 'aquilla/icons/actions/Forward';
import Button from 'aquilla/core/molecules/Button';
import CloudUpload from '@icon/CloudDownload';
import { NEOLESSON } from '@utils/constants';
import { PDF_DOWNLOAD, SPECIAL_CLASS_NOTIFY } from '@constants/segment';
import { segment } from '@actions/analytics';
import segmentStorage from '@events/segmentStorage';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  getIsLoggedIn,
  getIsPlatformUnlocked,
  getMe
} from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { DRAWER_IDS, unlockFreePlanFor } from '@constants/index';
import Divider from 'aquilla/core/atoms/Divider';
import { getFullDate } from '@utils/dates';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import useAppInstallAnalytics from '@hooks/useAppInstallAnalytics';
import { Play } from 'aquilla/icons/actions/Play';
import useCheckFeatureLimit from '@cont/DeviceLimitation/useCheckFeatureLimit';
import { FeatureType } from 'types/features';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';
import ClassVideoPlayer from '@cont/ClassVideoPlayer/ClassVideoPlayer';
// import Playlist from './playlists';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import NewBreadCrumbs from '@base/BreadCrumbs/NewBreadCrumbs';
import { isLegendEducator as getIsLegendEducator } from '@models/User';
import {
  getTopicGroup,
  isSummitClass as getIsSummitClass
} from '@models/Course';
import Dialog from '@comp/Dialog/Dialog';
import { Notify } from 'aquilla/icons/actions/Notify';
import { Notified } from 'aquilla/icons/misc/Notified';
import useMounted from '@hooks/useMounted';
import { createPortal } from 'react-dom';
import useFollowGoal from '@api/hooks/useFollowGoal';
import { css } from '@emotion/react';
import { abVariations } from '@utils/abHelper';
import NotifyMe from './NotifyMe';
import DownloadAppDialog from './DownloadAppDialog';
import Preview from './Preview';
import ClassExperimentContext from './ClassExpContext';
import PreviewNew from './PreviewNew';
import Playlist from './playlists';

const Container = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_10B} 0 ${Spacings.SPACING_6B};
  }
`;
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 720px 1fr;
  grid-template-areas: 'nav playlist' 'class playlist';

  ${(props) =>
    props.$isTest
      ? css`
          grid-template-columns: 720px 1fr;
          grid-template-areas: 'nav playlist' 'class playlist';
        `
      : css`
          grid-template-columns: 1fr;
          grid-template-areas: 'nav' 'class';
        `}

  ${mediaQuery.uptoTablet} {
    grid-template-columns: 1fr;
    grid-template-areas: 'nav' 'class';
  }
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const PlaylistContainer = styled.div`
  grid-area: playlist;
  margin-left: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;
const ClassDetails = styled.div`
  grid-area: class;
`;
const PreviewWrapper = styled.div`
  position: relative;
  margin-bottom: ${Spacings.SPACING_4B};
`;
const VideoPlayerWrapper = styled.div`
  border: ${Spacings.SPACING_1} solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_4B};
  overflow: hidden;
  display: flex;
  height: ${({ $isTest }) => ($isTest ? '472px' : '521px')};
  margin-bottom: ${Spacings.SPACING_4B};

  iframe {
    position: relative;
    height: 100%;
    width: 100%;
    z-index: 1;
  }
`;
const MetaInfo = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
  align-items: center;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const LanguageTag = styled(Tag)``;
const Subject = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: 12px;
  text-transform: uppercase;
`;

const ClassTitle = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_4B};
`;
const GridContainer2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: 'educator buttons' 'divider divider' 'desc desc';

  @media only screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'educator' 'divider' 'desc' 'buttons';
  }
`;
const EducatorInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  float: left;
  grid-area: educator;
`;
const ClassButtonWrapper = styled.div`
  float: right;
  display: flex;
  gap: 12px;
  grid-area: buttons;
  align-items: center;
  justify-content: flex-end;

  @media only screen and (max-width: 768px) {
    justify-content: flex-start;
    margin-top: ${Spacings.SPACING_4B};
  }
`;
const StyledButton = styled(Button)`
  width: max-content;
  justify-content: center;
  align-items: center;
  [class*='button--right-icon'] {
    display: inline-flex;
    margin: 0;
  }
  gap: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
    min-width: 0;
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
    gap: ${Spacings.SPACING_0B};
    [class*='button--label-content'] {
      display: none;
    }
  }
`;
const ShareButton = styled(StyledButton)`
  gap: ${Spacings.SPACING_0B};
  justify-content: center;
  align-items: center;
  min-width: max-content;
  width: ${Spacings.SPACING_10B};
  padding: unset;
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_0B};
    min-width: 0;
  }
`;
const WatchStyledButton = styled(Button)`
  gap: ${Spacings.SPACING_1B};
  min-width: 224px;
  height: ${Spacings.SPACING_10B};
  width: max-content;
  display: none;
  ${mediaQuery.uptoTablet} {
    min-width: 178px;
    display: block;
  }
`;

const NotifyStyledButton = styled(Button)`
  gap: ${Spacings.SPACING_1B};
  min-width: 224px;
  height: 48px;
  width: max-content;
  display: none;
  ${mediaQuery.uptoMobile} {
    min-width: calc(100% - 62px);
    display: flex;
    align-items: center;
    padding: unset;
    [class*='icon'] {
      display: flex;
    }
  }
`;
const Description = styled.div`
  clear: both;
  grid-area: desc;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
    -webkit-line-clamp: 2;
  }
`;

const DescriptionTruncated = styled(Typography)`
  display: inline-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    -webkit-line-clamp: ${({ $isExpanded }) => ($isExpanded ? 'unset' : '2')};
  }
`;
const InfoWrapper = styled.div``;

const Name = styled(Typography)``;
const Info = styled(Typography)``;
const ClassDuration = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_4B} 0;
  grid-area: divider;

  ${mediaQuery.uptoTablet} {
    margin: ${Spacings.SPACING_2B} 0;
  }
`;

const ReedMoreCTA = styled.div`
  position: relative;
  display: none;
  text-transform: uppercase;
  font-weight: ${Fonts.HEAVY};
  color: var(--color-text-primary);
  margin-left: ${Spacings.SPACING_1B};

  ${mediaQuery.uptoMobile} {
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'inline-block')};
  }
`;

const EducatorInfo = ({ author = {}, topicGroupName }) => {
  const authorFullName = getAuthorName(author);
  const { levelInfo } = author;
  return (
    <EducatorInfoWrapper>
      <Link href="/[authorID]" as={`/@${author.username}`}>
        <EducatorAvatar
          src={author.avatar}
          levelInfo={{
            ...(levelInfo || {}),
            currentLevelId: levelInfo?.levelId
          }}
          size="S"
        />
      </Link>
      <InfoWrapper>
        <Link href="/[authorID]" as={`/@${author.username}`}>
          <Name variant="h6">{authorFullName}</Name>
        </Link>
        {topicGroupName && (
          <Info variant="p3" color="var(--color-text-secondary)">
            {author.followersCount && (
              <>{`${nFormatter(author.followersCount)} followers • `}</>
            )}
            {`${topicGroupName}`}
          </Info>
        )}
      </InfoWrapper>
    </EducatorInfoWrapper>
  );
};

const NewHeader = () => {
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const {
    query: { uid: classUID, isSyllabusFeed: isSyllabusFeedQuery }
  } = useRouter();
  const [isUpcoming, setIsUpcoming] = useState(false);
  const [isClassHappenedAndNotRecorded, setIsClassHappenedAndNotRecorded] =
    useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [objectUid, setObjectUid] = useState(null);
  const [objectType, setObjectType] = useState(null);
  const [readMoreCTA, setReadMoreCTA] = useState(true);
  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);
  const { classInfo, isValidating } = useClassInfo(classUID, {
    revalidateOnMount: true
  });
  const { course: courseInfo, onToggleEnroll: onToggleCourseEnroll } =
    useCourseInfo(classInfo.programme?.uid);
  const me = useMyInfo(getMe);
  const [description, setDescription] = useState(courseInfo?.description);
  const isFreeLesson = isSyllabusFeedQuery === 'true';
  const { isEnrolled = false, goal = {} } = courseInfo;
  const author = classInfo.author || {};
  const learners = nFormatter(
    (isUpcoming ? classInfo.totalEnrolledLearners : classInfo.attendance) || 0,
    1
  );
  const isGTP = useConstants(getIsGTPGoal(goal.uid));
  const isNeoLesson = classInfo.classType === NEOLESSON;
  const { color } = getTopicGroupAssetObj(
    classInfo?.programme?.topicGroups[0]?.name
  );
  const {
    programme: { isSpecial }
  } = classInfo;
  const goalSegmentData = useGoalEventProps({ goalUID: goal.uid });
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isPlatformUnlocked = useMyInfo(getIsPlatformUnlocked);
  const fpuCallback = useRef();
  const isClassClicked = useRef();
  const [isAppDownloadDialog, setIsAppDownloadDialog] = useState(false);
  const appDownloadLink = useOneLinkSmartScriptV2({
    campaign: 'class page'
  });
  const sendAapInstallAnalytics = useAppInstallAnalytics();
  const { open: openClassPlayer } = useCourseClassPlayer();
  const { isOpen: isPlayerOpen } = useToggleable(
    DRAWER_IDS.COURSE_CLASS_PLAYER
  );
  const isMounted = useMounted();
  const { followCurrentGoal } = useFollowGoal(goal.uid);
  const isRequestingDownloadNotes = useRef(false);
  const isRequestingPlayClass = useRef(false);

  useEffect(() => {
    if (isLoggedIn && isClassClicked.current) {
      openClassPlayer({
        lesson: classInfo,
        course: courseInfo,
        byPassCheck: isFreeLesson,
        isFreeLesson,
        forceFetch: isFreeLesson
      });
      isClassClicked.current = false;
    }
    setIsUpcoming(getIsUpcoming(classInfo));
  }, [classInfo, courseInfo, isFreeLesson, isLoggedIn, openClassPlayer]);

  useEffect(() => {
    setIsUpcoming(getIsUpcoming(classInfo));
    if (classInfo && !isValidating) {
      setIsClassHappenedAndNotRecorded(isCompletedWithNoVideoUrl(classInfo));
    } else {
      setIsClassHappenedAndNotRecorded(false);
    }
  }, [classInfo, isValidating]);
  useEffect(() => {
    if (
      isLoggedIn &&
      (isRequestingDownloadNotes.current || isRequestingPlayClass.current)
    ) {
      isRequestingDownloadNotes.current = false;
      isRequestingPlayClass.current = false;
      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const getInfoString = (lesson) => {
    if (classDidNotHappen(lesson))
      return `${getFullDate(lesson.liveAt)} • ${classCancelledMsg}`;
    return getSpecialInfoString(lesson);
  };

  const {
    topicGroup,
    tags,
    isLive,
    infoString,
    isLegendEducator,
    isSummitClass
  } = useMemo(() => {
    return {
      topicGroup: getTopicGroup(courseInfo),
      tags: getClass(classInfo, isGTP),
      isLive: isCurrentlyLive(classInfo),
      infoString: getInfoString(classInfo),
      isLegendEducator: getIsLegendEducator(classInfo.author),
      isSummitClass: getIsSummitClass(courseInfo)
    };
  }, [classInfo, courseInfo, isGTP]);

  const { expVar: expVariation, isFetched } = useContext(
    ClassExperimentContext
  );
  const isTest = isFetched && expVariation !== abVariations.CONTROL;

  const handleNeoLessonPdfClick = () => {
    segment.track(PDF_DOWNLOAD, {
      ...goalSegmentData,
      download_type: 'With annotations',
      lesson_type: 'Recorded',
      is_annotation_on: true,
      last_primary_source: segmentStorage.getItem('lps'),
      lesson_uid: classInfo.uid,
      lesson_title: classInfo.name,
      educator_uid: classInfo.author?.uid,
      educator_username: classInfo.author?.name
    });
    window.open(classInfo.slidesPdf?.withAnnotation);
  };

  const onNotesClick = (event) => {
    if (isUpcoming) {
      return;
    }

    if (isLoggedIn) {
      followCurrentGoal();
      isRequestingDownloadNotes.current = false;
      if (isPlatformUnlocked || isGTP) {
        setAnchorEl(event.currentTarget);
      } else {
        setObjectUid(classInfo.uid);
        setObjectType(unlockFreePlanFor.DOWNLOAD_PDF);
        setFreePlanUnlockDrawerState(true);
        fpuCallback.current = () => setAnchorEl(event.currentTarget);
        setFreePlanUnlockDrawerState(true);
      }
    } else {
      isRequestingDownloadNotes.current = true;
      openLoginDrawer();
    }
  };
  const handleClose = () => setAnchorEl(null);
  const handleShare = () => setShowShareOptions(true);
  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);
  const closeShareOptions = () => setShowShareOptions(false);
  const onClose = () => {
    setIsAppDownloadDialog(false);
    isClassClicked.current = false;
  };
  const buttonLabel = 'Watch on the app';
  const downloadApp = () => {
    sendAapInstallAnalytics({
      button_text: buttonLabel
    });
    window.open(appDownloadLink, '_blank');
  };
  const handleLessonPdfClick = () => {
    segment.track(PDF_DOWNLOAD, {
      ...goalSegmentData,
      download_type: 'No annotations',
      lesson_type: 'Recorded',
      is_annotation_on: false,
      last_primary_source: segmentStorage.getItem('lps'),
      lesson_uid: classInfo.uid,
      lesson_title: classInfo.name,
      educator_uid: classInfo.author?.uid,
      educator_username: classInfo.author?.name
    });
    window.open(classInfo.slidesPdf?.noAnnotation);
  };
  const handleUnlockedClassClick = useCheckFeatureLimit(FeatureType.CLASS, () =>
    openClassPlayer({
      lesson: classInfo,
      course: courseInfo,
      byPassCheck: isFreeLesson,
      isFreeLesson,
      forceFetch: isFreeLesson
    })
  );
  const onItemClick = (lpss) => {
    if (isUpcoming) {
      return;
    }

    segmentStorage.setItem('lpss', lpss || 'main CTA');
    if (isLoggedIn) {
      followCurrentGoal();
      isRequestingPlayClass.current = false;
      if (isPlatformUnlocked || isGTP) {
        handleUnlockedClassClick();
      } else {
        setObjectUid(classInfo.uid);
        setObjectType(unlockFreePlanFor.SPECIAL_CLASS);
        fpuCallback.current = () => {
          openClassPlayer({
            lesson: classInfo,
            course: courseInfo,
            byPassCheck: isFreeLesson,
            isFreeLesson,
            forceFetch: isFreeLesson
          });
        };
        setFreePlanUnlockDrawerState(true);
      }
    } else {
      openLoginDrawer();
      isClassClicked.current = true;
      isRequestingPlayClass.current = true;
    }
  };
  const handleSeeMoreClick = () => {
    setDescription(courseInfo?.description);
    setReadMoreCTA(false);
  };
  const onNotify = () => {
    if (!isLoggedIn) {
      openLoginDrawer();
    }

    if (!isPlatformUnlocked && !isGTP && isLoggedIn) {
      setObjectUid(classInfo.programme?.uid);
      setObjectType(unlockFreePlanFor.SPECIAL_CLASS_NOTIFY);
      setFreePlanUnlockDrawerState(true);
      fpuCallback.current = onToggleCourseEnroll;
    } else {
      segment.track(SPECIAL_CLASS_NOTIFY, {
        lesson_state: classInfo.state,
        lesson_title: classInfo.name,
        lesson_uid: classInfo.uid,
        educator_uid: classInfo.author.uid,
        educator_username: classInfo.author.username,
        goal_name: courseInfo.goal.name,
        goal_uid: courseInfo.goal.uid,
        is_subscription_active: hasActiveSubscriptionToGoal(
          me,
          courseInfo.goal.uid
        ),
        language: me.language,
        last_primary_source_section: 'Special Class Header',
        start_date: 'NA',
        subscription_duration: 'NA',
        subscription_end_date: 'NA'
      });

      onToggleCourseEnroll();
    }
  };

  const getPlayer = () => {
    if (isSpecial) {
      return <ClassVideoPlayer />;
    }
    if (isMounted) {
      return createPortal(<ClassVideoPlayer />, document.body);
    }
    return null;
  };
  const PreviewComponent = isTest ? PreviewNew : Preview;
  return (
    <Container>
      <GridContainer $isTest={isTest}>
        {!isLegendEducator && !isSummitClass && (
          <TopBar>
            <NewBreadCrumbs tags={tags} onBackClick={goBack} />
          </TopBar>
        )}
        {isTest && (
          <PlaylistContainer>
            <Playlist author={author} classUID={classUID} />
          </PlaylistContainer>
        )}
        <ClassDetails>
          {isPlayerOpen ? (
            <VideoPlayerWrapper $isTest={isTest}>
              {getPlayer()}
            </VideoPlayerWrapper>
          ) : (
            <PreviewWrapper>
              <NotifyMe
                classInfo={classInfo}
                isUpcoming={isUpcoming}
                setIsUpcoming={setIsUpcoming}
                onNotify={onNotify}
                isEnrolled={isEnrolled}
                isClassHappenedAndNotRecorded={isClassHappenedAndNotRecorded}
              />
              <PreviewComponent
                author={courseInfo.authors[0]}
                topicGroupName={topicGroup?.name}
                classInfo={classInfo}
                learners={learners}
                infoString={infoString}
                isLive={isLive}
                isUpcoming={isUpcoming}
                onPlay={onItemClick}
                courseInfo={courseInfo}
                downloadApp={downloadApp}
                isClassHappenedAndNotRecorded={isClassHappenedAndNotRecorded}
              />
            </PreviewWrapper>
          )}
          <MetaInfo>
            <LanguageTag variant="default" size="small">
              {LANGUAGE_MAP[courseInfo?.language]?.languageText || 'EN'}
            </LanguageTag>
            <Subject color={color} variant="h4">
              {topicGroup?.name}
            </Subject>
          </MetaInfo>
          <ClassTitle variant="h3">{classInfo.name}</ClassTitle>
          <GridContainer2>
            <EducatorInfo author={author} topicGroupName={topicGroup?.name} />
            <ClassButtonWrapper>
              {!isUpcoming && (
                <WatchStyledButton
                  label={isLoggedIn ? 'Watch on app' : 'Watch now'}
                  onClick={isLoggedIn ? downloadApp : onItemClick}
                  size="small"
                  variant="primary"
                  startIcon={<Play color="primary" width="12" height="12" />}
                />
              )}
              {isUpcoming && (
                <NotifyStyledButton
                  label={isEnrolled ? 'We will notify you' : 'Notify me'}
                  onClick={onNotify}
                  size="large"
                  variant="primary"
                  startIcon={
                    isEnrolled ? (
                      <Notified
                        color="var(--color-i-gray-13)"
                        width="24"
                        height="24"
                      />
                    ) : (
                      <Notify color="primary" width="24" height="24" />
                    )
                  }
                  disabled={isEnrolled}
                />
              )}
              {!isUpcoming && (
                <>
                  <StyledButton
                    label="Download Class PDF"
                    onClick={
                      isNeoLesson ? handleNeoLessonPdfClick : onNotesClick
                    }
                    variant="secondary"
                    size="small"
                    startIcon={<CloudUpload height={24} width={24} />}
                  />
                  <Menu anchorEl={anchorEl} onClose={handleClose}>
                    <PDFDownload
                      course={courseInfo}
                      handleClose={handleClose}
                      selectedLesson={{
                        slides: {
                          withoutAnnotations:
                            classInfo?.slidesPdf?.noAnnotation,
                          withAnnotations: classInfo?.slidesPdf?.withAnnotation
                        }
                      }}
                      color="var(--color-text-primary)"
                    />
                  </Menu>
                </>
              )}
              <ShareButton
                label=""
                onClick={handleShare}
                variant="secondary"
                size="small"
                startIcon={<Forward color="primary" width="24" height="24" />}
              />
            </ClassButtonWrapper>
            <StyledDivider />
            <Description>
              <ClassDuration variant="p2" color="var(--color-text-secondary)">
                {`${infoString} • ${learners} ${
                  // eslint-disable-next-line no-nested-ternary
                  isUpcoming ? 'waiting' : isLive ? 'watching' : 'views'
                }`}
              </ClassDuration>
              <DescriptionTruncated
                variant="p2"
                color="var(--color-text-secondary)"
                $isExpanded={!readMoreCTA}
              >
                {description}
              </DescriptionTruncated>
              {readMoreCTA && (
                <ReedMoreCTA
                  $isHidden={!readMoreCTA}
                  onClick={handleSeeMoreClick}
                >
                  Read more
                </ReedMoreCTA>
              )}
            </Description>
          </GridContainer2>
        </ClassDetails>
      </GridContainer>
      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={objectType}
        objectUid={objectUid}
        callBack={() => {
          setTimeout(() => setFreePlanUnlockDrawerState(false), 3000);
          if (fpuCallback?.current) fpuCallback.current();
        }}
      />
      <Dialog onClose={closeShareOptions} dialogState={showShareOptions}>
        <ShareDialog data={classInfo} />
      </Dialog>
      <DownloadAppDialog
        isOpen={isAppDownloadDialog}
        onClose={onClose}
        downloadApp={downloadApp}
        onNotesClick={handleLessonPdfClick}
      />
    </Container>
  );
};

export default NewHeader;
